import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VendorService } from '@app/shared/services/vendor.service';
import { feedbackQuarters, stars } from '@app/shared/constants/constants';
import {
  contractorQuarterlyFeedback, ErrorMessages,
  NavigationRoutes,
  LoadingMessages,
  ButtonLabels,
  EmployeeProfile,
  ContractorListEnum,
  releaseInfo,
  PerformanceRating,
  PERFORMANCE_RATING_SHORT_LABEL,
  PERFORMANCE_RATING_OPTIONS,
  formatQuarterBoxLabel,
} from '@app/shared/constants/global.enum';
import {
  EmployeeDetailsResponse,
  ProfileItemConfig,
  mapEmployeeToProfileSections,
} from './vendor-contractor-profile.config';

interface FeedbackDTO {
  createdBy?: string | null;
  createdDate?: string | null;
  updatedBy?: string | null;
  updatedDate?: string | null;
  contractorFeedbackId?: number | null;
  employeeId?: string | null;
  quarter?: string | null;
  year?: number | null;
  techincalRating?: number | null;
  behaviouralRating?: number | null;
  performanceRating?: string | null;
  comments?: string | null;
  submittedBy?: string | null;
  submittedDate?: string | null;
}

interface QuarterBox {
  quarter: string;
  year: number;
  label: string;
  isCurrent: boolean;
  data: FeedbackDTO | null;
  feedbacks: FeedbackDTO[];
  loading: boolean;
  error?: string | null;
}

@Component({
  selector: 'app-vendor-contractor-profile',
  templateUrl: './vendor-contractor-profile.component.html',
  styleUrls: ['./vendor-contractor-profile.component.scss'],
})

export class VendorContractorProfileComponent implements OnInit {
  ContractorName: string = '';
  ContractorProfile: string = '';

  loading: boolean = false;
  error: string | null = null;
  hasData: boolean = false;


  aboutItems: ProfileItemConfig[] = [];
  teamInfoItems: ProfileItemConfig[] = [];
  performanceItems: ProfileItemConfig[] = [];
  skillItems: ProfileItemConfig[] = [];

  feedbackQuarters = feedbackQuarters;
  showFeedbackForm = false;
  stars = stars;
  feedbackForm!: FormGroup;
  currentQuarter = 3;
  submitted = false;
  quarterlyFeedback = contractorQuarterlyFeedback;
  quarterlyFeedbackEnum = contractorQuarterlyFeedback;
  vendorContractorProfile = ContractorListEnum;
  releaseEnum = releaseInfo;
  errorMessages = ErrorMessages;
  loadingMessages = LoadingMessages;
  buttonLabels = ButtonLabels;
  contractorListEnum = ContractorListEnum;
  quarterBoxes: QuarterBox[] = [];

  // DB-driven visible quarter set. null = fetch not yet completed → fallback
  // to showing all 4 boxes so we don't strand the UI on an empty screen.
  private visibleQuarterSet: Set<string> | null = null;
  @Input() contractorId: string | null = null;
  @Output() close = new EventEmitter<void>();

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private vendorService: VendorService
  ) {
    this.initializeFeedbackForm();
  }

  ngOnInit(): void {
    if (this.contractorId) {
      this.loadEmployeeDetails();
      this.ensureVisibleQuartersLoaded(() => {
        this.buildQuarterBoxes();
        this.fetchQuarterlyFeedback();
      });
    }
  }

  private ensureVisibleQuartersLoaded(cb: () => void): void {
    if (this.visibleQuarterSet !== null) { cb(); return; }
    this.vendorService.getVisibleFeedbackQuarters().subscribe({
      next: (list) => { this.visibleQuarterSet = new Set(list ?? []); cb(); },
      error: () => { this.visibleQuarterSet = new Set(); cb(); },
    });
  }

  private applyVisibleQuarterFilter(boxes: QuarterBox[]): QuarterBox[] {
    if (this.visibleQuarterSet === null) return boxes;
    const seen = new Set<string>();
    return boxes.filter((b) => {
      if (!this.visibleQuarterSet!.has(b.quarter)) return false;
      if (seen.has(b.quarter)) return false;
      seen.add(b.quarter);
      return true;
    });
  }

  private initializeFeedbackForm(): void {
    this.feedbackForm = this.fb.group({
      // AB: new-form Performance Rating bucket. Vendor-rep view is read-only
      // for feedback so this form isn't wired to a submit path — kept in sync
      // with the VM contractor-profile for consistency.
      performanceRating: [null, Validators.required],
      comments: ['', [Validators.required, Validators.minLength(1)]]
    });
  }

  /** Safe display-label lookup for a raw performanceRating string. Falls back
   *  to the raw value on unknown / null so untyped rows still render something. */
  getPerformanceRatingLabel(raw?: string | null): string {
    if (!raw) return '';
    const key = raw as PerformanceRating;
    return PERFORMANCE_RATING_SHORT_LABEL[key] ?? raw;
  }

  /** Full "A — High performer" display for historical / detail surfaces. */
  getPerformanceRatingFullDisplay(raw?: string | null): string {
    if (!raw) return '';
    const opt = PERFORMANCE_RATING_OPTIONS.find(o => o.value === raw);
    return opt ? opt.label : raw;
  }

  private loadEmployeeDetails(): void {
    if (!this.contractorId) {
      this.error = this.errorMessages.NoEmployeeId;
      return;
    }

    this.loading = true;
    this.error = null;
    this.hasData = false;

    this.vendorService.getEmployeeDetails(this.contractorId)
      .subscribe({
        next: (response) => {
          this.loading = false;

          if (response?.status === this.contractorListEnum.Success && response?.data) {
            this.mapEmployeeDataToItems(response.data);
            this.hasData = true;
          } else if (response?.status === this.contractorListEnum.Failed) {
            this.error = response?.message || `${this.errorMessages.EmployeeNotFound} with ID ${this.contractorId}`;
          } else {
            this.error = this.errorMessages.InvalidResponse;
          }
        },
        error: (error) => {
          this.loading = false;
          this.hasData = false;
        }
      });
  }

  private mapEmployeeDataToItems(data: EmployeeDetailsResponse): void {

    this.ContractorName = data.employeeName || '';
    this.ContractorProfile = data.jobRole || '';

    const profileSections = mapEmployeeToProfileSections(data);

    this.aboutItems = profileSections.about;
    this.teamInfoItems = profileSections.teamInfo;
    this.performanceItems = profileSections.performance;
    this.skillItems = profileSections.skills;
  }

  private buildQuarterBoxes(): void {
    const now = new Date();
    const boxes: QuarterBox[] = [];

    for (let i = 0; i < 4; i++) {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      d.setMonth(d.getMonth() - i * 3);
      const qNum = Math.floor(d.getMonth() / 3) + 1;
      boxes.push({
        quarter: `Q${qNum}`,
        year: d.getFullYear(),
        label: formatQuarterBoxLabel(qNum, d.getFullYear()),
        isCurrent: i === 0,
        data: null,
        feedbacks: [],
        loading: true,
        error: null
      });
    }

    this.quarterBoxes = this.applyVisibleQuarterFilter(boxes);
  }

  private fetchQuarterlyFeedback(): void {
    if (!this.contractorId) return;
    this.quarterBoxes.forEach(box => {
      box.loading = true;
      box.error = null;
      this.vendorService
        .getQuarterlyFeedback(this.contractorId as string, String(box.year), box.quarter)
        .subscribe({
          next: (resp: any) => {
            const payload = resp?.data ?? resp;
            const arr: FeedbackDTO[] = Array.isArray(payload)
              ? payload
              : (payload ? [payload] : []);
            arr.sort((a, b) =>
              new Date(b.submittedDate || 0).getTime() -
              new Date(a.submittedDate || 0).getTime()
            );
            box.feedbacks = arr;
            box.loading = false;
          },
          error: () => {
            box.feedbacks = [];
            box.error = 'Failed to load';
            box.loading = false;
          }
        });
    });
  }

  trackFeedback(index: number, fb: FeedbackDTO) {
    return fb.contractorFeedbackId ?? index;
  }

 private formatDate(input?: string | null): string | null {
    return input ? input.slice(0, 10) : null; // "YYYY-MM-DD"
  }
  navigateToTimesheet(): void {
    this.close.emit();
  }
}
